import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Volume2, VolumeX, Play } from "lucide-react";

/* Instagram-stories-style video lightbox: auto-advances to the next clip,
   story progress bars, tap left/right to skip, and a tap-to-unmute button. */
export const VideoReel = ({ clips, startIndex = 0, onClose }) => {
  const [i, setI] = useState(startIndex);
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const ref = useRef(null);

  const go = useCallback((n) => {
    setProgress(0);
    setPaused(false);
    setI((n + clips.length) % clips.length);
  }, [clips.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(i + 1);
      else if (e.key === "ArrowLeft") go(i - 1);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [i, go, onClose]);

  // keep the element's muted flag in sync (also across clip changes)
  useEffect(() => { if (ref.current) ref.current.muted = muted; }, [muted, i]);

  const clip = clips[i] || {};

  const togglePlay = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setPaused(false); }
    else { v.pause(); setPaused(true); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-[fadeIn_0.25s_ease]"
      data-testid="video-reel" onClick={onClose}>
      <div className="relative w-full max-w-[430px] px-3" onClick={(e) => e.stopPropagation()}>
        {/* story progress bars */}
        <div className="absolute inset-x-6 top-2 z-30 flex gap-1.5">
          {clips.map((_, idx) => (
            <span key={idx} className="h-[3px] flex-1 overflow-hidden rounded-full bg-white/25">
              <span className="block h-full rounded-full bg-white"
                style={{ width: idx < i ? "100%" : idx === i ? `${progress}%` : "0%", transition: "width 0.15s linear" }} />
            </span>
          ))}
        </div>

        {/* top controls */}
        <div className="absolute right-5 top-5 z-30 flex items-center gap-2">
          <button onClick={() => setMuted((m) => !m)} data-testid="reel-sound-toggle" aria-label={muted ? "Unmute" : "Mute"}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md transition-colors hover:bg-white/30">
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
          <button onClick={onClose} data-testid="reel-close" aria-label="Close"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md transition-colors hover:bg-white/30">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* player */}
        <div className="relative mt-6 overflow-hidden rounded-[1.75rem] bg-black shadow-[0_40px_90px_-30px_rgba(0,0,0,0.8)]" style={{ aspectRatio: "9 / 16" }}>
          <video ref={ref} key={clip.src} src={clip.src} poster={clip.poster} autoPlay playsInline muted={muted}
            data-testid="video-reel-player" className="h-full w-full object-cover"
            onTimeUpdate={(e) => { const v = e.currentTarget; if (v.duration) setProgress((v.currentTime / v.duration) * 100); }}
            onEnded={() => go(i + 1)} />

          {/* tap zones: left = prev, right = next, centre = play/pause */}
          <button aria-label="Previous clip" data-testid="reel-prev" onClick={() => go(i - 1)} className="absolute inset-y-0 left-0 z-10 w-1/3" />
          <button aria-label="Play or pause" onClick={togglePlay} className="absolute inset-y-0 left-1/3 z-10 w-1/3" />
          <button aria-label="Next clip" data-testid="reel-next" onClick={() => go(i + 1)} className="absolute inset-y-0 right-0 z-10 w-1/3" />

          {paused && (
            <span className="pointer-events-none absolute left-1/2 top-1/2 z-20 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-[#232222]">
              <Play className="ml-1 h-7 w-7 fill-[#232222]" />
            </span>
          )}

          {/* caption */}
          {(clip.title || clip.sub) && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/75 to-transparent p-5 pt-16">
              {clip.title && <p className="text-sm font-semibold text-white drop-shadow">{clip.title}</p>}
              {clip.sub && <p className="mt-0.5 text-xs text-white/85 drop-shadow">{clip.sub}</p>}
            </div>
          )}

          {/* tap-for-sound cue while muted */}
          {muted && (
            <button onClick={() => setMuted(false)} data-testid="reel-unmute-cue"
              className="absolute left-1/2 top-4 z-30 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-white/90 px-3.5 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-[#232222] shadow-lg animate-[fadeIn_0.4s_ease]">
              <VolumeX className="h-3.5 w-3.5" /> Tap for sound
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
